import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, detailPrimaryKind, detailStateKind } from './tasks-help.shared';

/**
 * ?-Hilfe fuer den Aufgaben-Detail-Dialog — die adaptivste Flaeche des Moduls.
 *
 * **Fast jede Station ist zustandsabhaengig.** Hinweiszeile, Beschreibung,
 * Checkliste, Anhaenge, Angebots-Chips, Kette und die gesamte Fussleiste koennen
 * fehlen; immer da sind nur Details (erste Zeile „Status" als Pille) und
 * Aktivitaet (Render-Bedingungen in `task-detail-dialog.component.ts`). Deshalb
 * `renumberVisibleSteps` — ohne das zaehlte das Popover "2 / 11 → 5 / 11".
 *
 * Traegt drei fachliche Kernaussagen:
 *  - **"Zustaendig" ist nicht "Sichtbar fuer".** Wer die Aufgabe sieht, ist
 *    dadurch nicht zustaendig (`buildDetailMeta()`, feste Reihenfolge).
 *  - **Ein Anhang ersetzt das Abhaken nicht.** Der Nachweis haengt am Punkt,
 *    die Anhangsliste ist etwas anderes (`TASK_ATTACHMENTS_NOT_A_PROOF`).
 *  - **Es gibt genau EINE Primaeraktion, und der Server bestimmt sie**
 *    (`primary-action.ts`).
 *
 * **Reihenfolge = Dialog von oben nach unten (Stand P34, 08.10.2026):**
 * welcome · state (Hinweiszeile) · description · checklist · attachments · offers
 * (Chips „Hinzufuegen") · timeline (Aktivitaet) · meta (Details-Spalte) ·
 * dependencies (Kette) · primary (Fuss) · menu (⋯ im Kopf). Am Telefon stehen die
 * Details kompakt ueber dem Hinweis — dort springt `dt-meta` einmal nach oben;
 * dafuer bleibt die Reihenfolge bei 1440 eine Bewegung links → rechts.
 *
 * **Varianten statt Stationen** (Hoechstzahl elf, Spec `tasks-help-tours`): Die
 * Hinweiszeile hat zwei Texte (allgemein / „Gemeldet"), die Primaeraktion fuenf
 * (Basis, Nachweis, gesperrt, „Wieder oeffnen", Abnahme). Ein Schritt bedeutet
 * einen stabilen Zaehler; je Variante ein eigener Schritt mit `skipIf` waere
 * dieselbe Aussage mehrfach. Titel, Text UND Tonspur folgen der Variante.
 *
 * **Tonspuren (Task 13, 08.10.2026):** Jede Station mit neuem Sprechtext hat eine
 * neue Audio-ID (`…-v2` bzw. neu). Bis zur Vertonung gibt es dafuer keine MP3 —
 * `TourAudioService.exists()` blendet den Abspieler aus, die Station laeuft stumm.
 *
 * **Popover in der Details-Spalte links davon (A-8).** `popoverOutsideDialog`
 * fand bei 1440 neben dem breiten Dialog keinen Platz und klemmte die Karte an
 * den rechten Rand — genau ueber die Details-Spalte, die sie erklaerte.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

/** Wie {@link dialogStep}, aber die Karte haengt am Element (`placement` gilt). */
const dialogStepAnchored = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
} as const;

/**
 * Varianten der Primaeraktion — aus `data-primary-kind` der Fussleiste bzw. den
 * Abnahme-Knoepfen.
 *
 * - `approval`: „Abnehmen"/„Zurueckweisen" stehen im Fuss (Vorrang: das ist dann die
 *   Entscheidung, um die es geht; die Station hiess bis 08.10.2026 `dt-approval`).
 * - `proof`: `capture-proof`, es fehlt ein Pflichtnachweis, der Knopf springt zum Punkt.
 * - `blocked`: `complete-blocked`, der Knopf ist gesperrt, den Grund nennt der Hinweis.
 * - `reopen`: erledigte Aufgabe, unten steht nur „Wieder oeffnen" (A-4).
 * - `base`: alles andere.
 *
 * ⚠️ `resolve-blocked` gibt es hier nicht (A-5): bei „Gemeldet" hat der Dialog KEINEN
 * Fuss (`hasFooterActions`), „Problem aufloesen" liegt in der Hinweiszeile.
 */
export type DetailPrimaryVariant = 'base' | 'proof' | 'blocked' | 'reopen' | 'approval';

export function detailPrimaryVariant(
  kind: string | null = detailPrimaryKind(),
  approvable: boolean = !anchorMissing(AnchorId.TaskDetailApprove)
): DetailPrimaryVariant {
  if (approvable) return 'approval';
  if (kind === 'capture-proof') return 'proof';
  if (kind === 'complete-blocked') return 'blocked';
  if (kind === 'reopen') return 'reopen';
  return 'base';
}

/**
 * Text- und Tonspur je Variante. `proof` und `approval` behalten ihre vertonte Spur
 * (Text unveraendert, die Karte wurde auf den Ton gezogen); die anderen haben neue IDs.
 */
export const DETAIL_PRIMARY_VARIANTS: Readonly<
  Record<DetailPrimaryVariant, { suffix: string; audioStepId: string }>
> = {
  base: { suffix: '', audioStepId: 'dt-primary-v2' },
  proof: { suffix: '_PROOF', audioStepId: 'dt-primary-proof' },
  blocked: { suffix: '_BLOCKED', audioStepId: 'dt-primary-blocked-v2' },
  reopen: { suffix: '_REOPEN', audioStepId: 'dt-primary-reopen' },
  approval: { suffix: '_APPROVAL', audioStepId: 'dt-approval' },
};

function primaryVariant() {
  return DETAIL_PRIMARY_VARIANTS[detailPrimaryVariant()];
}

/**
 * Varianten der Hinweiszeile (`data-state-kind`): „Gemeldet" hat einen eigenen Text
 * (Grund, Person, „Problem aufloesen"), alle anderen Arten den allgemeinen.
 */
export type DetailStateVariant = 'base' | 'reported';

export const DETAIL_STATE_VARIANTS: Readonly<
  Record<DetailStateVariant, { suffix: string; audioStepId: string }>
> = {
  base: { suffix: '', audioStepId: 'dt-state' },
  reported: { suffix: '_REPORTED', audioStepId: 'dt-state-reported' },
};

export function detailStateVariant(kind: string | null = detailStateKind()): DetailStateVariant {
  return kind === 'reported' ? 'reported' : 'base';
}

function stateVariant() {
  return DETAIL_STATE_VARIANTS[detailStateVariant()];
}

export const TASK_DETAIL_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskDetailHelp,
  version: 1,
  title: 'TASK_DETAIL_HELP_TOUR_TITLE',
  description: 'TASK_DETAIL_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Opener ist der Titel-Button der ersten Zeile — nur der oeffnet den Dialog,
  // ein Klick auf die Zeile selbst nicht. `settleMs` grosszuegig, weil das
  // Detail per HTTP nachgeladen wird.
  launchContext: {
    route: '/tasks/overview',
    opens: { openerAnchor: AnchorId.TasksRowOpen, settleMs: 600 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 170,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_DETAIL_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'dt-welcome',
          anchor: AnchorId.TaskDetailDialog,
          spotlightAnchor: AnchorId.TaskDetailDialog,
          title: 'TUTORIAL.TASK_DETAIL.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.WELCOME.CONTENT',
          audioStepId: 'dt-welcome-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            ...dialogStep,
            waitForOpen: true,
            outsideDialog: true,
            highlightDialogHeader: true,
          },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Die Hinweiszeile (P34, ersetzt die Zustandskarte) — nur, wenn sie etwas
          // sagt. „Gemeldet" ist eine Textvariante, erkannt an `data-state-kind`.
          id: 'dt-state',
          anchor: AnchorId.TaskDetailStateCard,
          spotlightAnchor: AnchorId.TaskDetailStateCard,
          title: () => `TUTORIAL.TASK_DETAIL.STATE.TITLE${stateVariant().suffix}`,
          content: () => `TUTORIAL.TASK_DETAIL.STATE.CONTENT${stateVariant().suffix}`,
          audioStepId: () => stateVariant().audioStepId,
          get tone() {
            return detailStateVariant() === 'reported' ? ('warning' as const) : undefined;
          },
          placement: 'top',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailStateCard),
        },
        {
          id: 'dt-description',
          anchor: AnchorId.TaskDetailDescription,
          spotlightAnchor: AnchorId.TaskDetailDescription,
          title: 'TUTORIAL.TASK_DETAIL.DESCRIPTION.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.DESCRIPTION.CONTENT',
          audioStepId: 'dt-description-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailDescription),
        },
        {
          id: 'dt-checklist',
          anchor: AnchorId.TaskDetailChecklist,
          spotlightAnchor: AnchorId.TaskDetailChecklist,
          title: 'TUTORIAL.TASK_DETAIL.CHECKLIST.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.CHECKLIST.CONTENT',
          audioStepId: 'dt-checklist-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailChecklist),
        },
        {
          // Kern-Step: Anhang ist kein Nachweis. Seit P34 UNTER Checkliste und
          // Nachweis. Ohne Datei fehlt der Abschnitt — dann erklaert `dt-offers`
          // den Chip „Anhang", der ihn oeffnet (bis 08.10.2026 zeigte diese Station
          // ersatzweise selbst auf den Chip und erzaehlte von Kacheln, die es nicht gab).
          id: 'dt-attachments',
          anchor: AnchorId.TaskDetailAttachments,
          spotlightAnchor: AnchorId.TaskDetailAttachments,
          title: 'TUTORIAL.TASK_DETAIL.ATTACHMENTS.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.ATTACHMENTS.CONTENT',
          audioStepId: 'dt-attachments-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailAttachments),
        },
        {
          // Leere Bereiche sind Chips („Notiz · Label · Anhang · Abhaengigkeit ·
          // Folgeaufgabe", je nach Schalter und Recht). Der Cue „Anhang" pulst den
          // Chip `task-detail-offer-attachment`, wenn er steht.
          id: 'dt-offers',
          anchor: AnchorId.TaskDetailOffers,
          spotlightAnchor: AnchorId.TaskDetailOffers,
          title: 'TUTORIAL.TASK_DETAIL.OFFERS.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.OFFERS.CONTENT',
          audioStepId: 'dt-offers',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailOffers),
        },
        {
          id: 'dt-timeline',
          anchor: AnchorId.TaskDetailTimeline,
          spotlightAnchor: AnchorId.TaskDetailTimeline,
          title: 'TUTORIAL.TASK_DETAIL.TIMELINE.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.TIMELINE.CONTENT',
          audioStepId: 'dt-timeline-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Kern-Step: Status-Pille, „Zustaendig" (direkt zuweisen) und „Sichtbar fuer".
          // Der Text nennt „Sichtbar fuer" bedingt („Steht dort auch …") — die Zeile
          // fehlt bei zugewiesenen Aufgaben, „Zustaendig" bei erledigten („Erledigt
          // von"). Ein Cue auf eine fehlende Zeile bleibt still; deshalb laeuft die
          // Station, sobald die Details stehen (immer), statt nur bei beiden Zeilen.
          id: 'dt-meta',
          anchor: AnchorId.TaskDetailMeta,
          spotlightAnchor: AnchorId.TaskDetailMeta,
          title: 'TUTORIAL.TASK_DETAIL.META.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.META.CONTENT',
          audioStepId: 'dt-meta-v2',
          placement: 'left',
          panelIndex: -1,
          dialogContext: dialogStepAnchored,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailMeta),
        },
        {
          // Direkt nach dt-meta: die Kette steht in der Details-Spalte unter den
          // Details (schmal unter der Aktivitaet). Popover links davon (A-8).
          // Die Sektion haengt an einer mehrgliedrigen ODER-Bedingung
          // (`showChain()`). Bewusst ueber DOM-Existenz geprueft:
          // die Komponente ist die einzige Wahrheit, eine nachgebaute
          // Bedingung wuerde beim naechsten Umbau still auseinanderlaufen.
          id: 'dt-dependencies',
          anchor: AnchorId.TaskDetailDependencies,
          spotlightAnchor: AnchorId.TaskDetailDependencies,
          title: 'TUTORIAL.TASK_DETAIL.DEPENDENCIES.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.DEPENDENCIES.CONTENT',
          audioStepId: 'dt-dependencies-v2',
          placement: 'left',
          panelIndex: -1,
          dialogContext: dialogStepAnchored,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailDependencies),
        },
        {
          // Kern-Step mit fuenf Textvarianten (s. Kopfkommentar). Der Spotlight
          // rahmt die ganze Fussleiste, das Popover haengt am Knopf selbst — bei der
          // Abnahme an „Abnehmen", „Zurueckweisen" leuchtet mit.
          id: 'dt-primary',
          get anchor() {
            return detailPrimaryVariant() === 'approval'
              ? AnchorId.TaskDetailApprove
              : AnchorId.TaskDetailPrimary;
          },
          spotlightAnchor: AnchorId.TaskDetailFooter,
          get additionalSpotlightAnchors() {
            return detailPrimaryVariant() === 'approval' ? [AnchorId.TaskDetailReject] : [];
          },
          title: () => `TUTORIAL.TASK_DETAIL.PRIMARY.TITLE${primaryVariant().suffix}`,
          content: () => `TUTORIAL.TASK_DETAIL.PRIMARY.CONTENT${primaryVariant().suffix}`,
          audioStepId: () => primaryVariant().audioStepId,
          placement: 'top',
          align: 'end',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () =>
            anchorMissing(AnchorId.TaskDetailPrimary) &&
            anchorMissing(AnchorId.TaskDetailApprove),
        },
        {
          // ⋯ steht seit ENT4 im KOPF oben rechts — die Karte darunter (A-8).
          id: 'dt-menu',
          anchor: AnchorId.TaskDetailMenu,
          spotlightAnchor: AnchorId.TaskDetailMenu,
          title: 'TUTORIAL.TASK_DETAIL.MENU.TITLE',
          content: 'TUTORIAL.TASK_DETAIL.MENU.CONTENT',
          audioStepId: 'dt-menu-v2',
          placement: 'bottom',
          align: 'end',
          panelIndex: -1,
          dialogContext: dialogStepAnchored,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskDetailMenu),
        },
      ],
    },
  ],
};
